import { Task, Update, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import { Effect } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { SdkClient, Snackbar } from '~/effects'
import { observeCurrentUser, observeRoute, runSdkRequest } from '~/features/module-runtime'
import { oneHourCourseId, oneHourCourseLessonIds } from './one-hour-course-manifest'

const observeCourseRoute = observeRoute({ name: 'one_hour_course' })
type ObservedCourseRoute = TaskMessage<typeof observeCourseRoute>['route']

type CourseCompletion =
  | { status: 'incomplete' }
  | { status: 'saving' }
  | { status: 'failed' }
  | { status: 'completed' }

type LessonWrite = {
  lessonId: string
  status: 'saving' | 'saved' | 'failed'
}

type CourseProgress =
  | { status: 'loading' }
  | { status: 'loadFailed'; completedLessonIds: string[] }
  | { status: 'ready'; completedLessonIds: string[] }
  | { status: 'saving'; baseCompletedLessonIds: string[]; writes: LessonWrite[] }
  | { status: 'reconciling'; completedLessonIds: string[] }

type InactiveCoursesState = {
  status: 'inactive'
  route: ObservedCourseRoute
  currentUserId: string | null
}

type CourseSession = {
  currentUserId: string
  progress: CourseProgress
  completion: CourseCompletion
}

type ActiveCoursesState = CourseSession & {
  status: 'active'
  route: NonNullable<ObservedCourseRoute>
}
type FinishingCoursesState = CourseSession & { status: 'finishing'; route: null }

export type CoursesState = InactiveCoursesState | ActiveCoursesState | FinishingCoursesState

const loadCourseProgress = Task.define((_userId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const progress = yield* runSdkRequest(
      sdk.send({ type: 'courses/getCourseProgress', courseId: oneHourCourseId })
    )
    if (!progress.ok) return { type: 'progressLoadFailed' } as const
    return {
      type: 'progressLoaded',
      completedLessonIds: progress.response.completedLessonIds,
      completed: progress.response.completed
    } as const
  })
)

export type CoursesMessage =
  | TaskMessage<typeof observeCourseRoute>
  | TaskMessage<typeof observeCurrentUser>
  | TaskMessage<typeof loadCourseProgress>
  | { type: 'lessonCompleted'; lessonId: string }
  | { type: 'retryProgressLoad' }
  | { type: 'lessonWriteSettled'; lessonId: string; result: 'saved' | 'failed' }
  | { type: 'courseCompletionSettled'; result: 'completed' | 'failed' }
  | { type: 'retryMarkCourseCompleted' }

/**
 * Course progress topology:
 *
 * inactive --route + logged in--> active(loading) --loaded--> active(ready)
 *    ^                                    |                          |
 *    |                                  failed                       | lesson intent(s)
 *    |                                    v                          v
 *    |                           active(loadFailed) <---- active(reconciling)
 *    |                                    | retry                     ^
 *    |                                    +--------------------------+ all writes settle
 *    +---------------- route leaves ------------------------- active(saving)
 *                                                              |
 *                                                              v
 *                                                         finishing(write)
 *
 * Route, auth, and progress loading are state-owned Tasks. Leaving the page
 * revokes its read. Accepted durable writes are Commands, so leaving the page
 * moves the chart to `finishing` without canceling them. Changing identities
 * discards the old presentation while the already-started write finishes.
 * Different lesson writes may run concurrently. While the page is open, the
 * final Task reload makes the server response authoritative. A failed read
 * stays explicit and retryable; it is never presented as empty progress.
 */
export const coursesModule = defModule<{ state: CoursesState; message: CoursesMessage }>({
  init: () => ({
    status: 'inactive',
    route: null,
    currentUserId: null
  }),
  tasks: coursesTasks,
  update: Update.ignoreWhen<CoursesState, CoursesMessage>(
    isMatching(
      P.union(
        {
          state: { status: 'inactive' },
          message: {
            type: P.union('lessonWriteSettled', 'courseCompletionSettled')
          }
        },
        {
          state: {
            status: P.union('active', 'finishing'),
            progress: { status: P.not('saving') }
          },
          message: { type: 'lessonWriteSettled' }
        },
        {
          state: {
            status: P.union('active', 'finishing'),
            completion: { status: P.not('saving') }
          },
          message: { type: 'courseCompletionSettled' }
        }
      )
    )
  )(updateCourses)
})

function coursesTasks(state: CoursesState) {
  const observations = [observeCourseRoute(), observeCurrentUser()]
  if (
    state.status == 'active' &&
    (state.progress.status == 'loading' || state.progress.status == 'reconciling')
  ) {
    return [...observations, loadCourseProgress(state.currentUserId)]
  }
  return observations
}

function updateCourses(
  state: CoursesState,
  message: CoursesMessage
): UpdateResult<CoursesState, CoursesMessage> {
  return match({ state, message })
    .returnType<UpdateResult<CoursesState, CoursesMessage>>()
    .with({ message: { type: 'routeChanged' } }, ({ state, message }) =>
      synchronizeCourse(state, message.route, state.currentUserId)
    )
    .with({ message: { type: 'currentUserChanged' } }, ({ state, message }) =>
      synchronizeCourse(state, state.route, message.currentUserId)
    )
    .with(
      {
        state: {
          status: 'active',
          progress: { status: P.union('loading', 'reconciling') }
        },
        message: { type: 'progressLoaded' }
      },
      ({ state, message }) => progressLoaded(state, message)
    )
    .with(
      {
        state: {
          status: 'active',
          progress: { status: P.union('loading', 'reconciling') }
        },
        message: { type: 'progressLoadFailed' }
      },
      ({ state }) => progressLoadFailed(state)
    )
    .with(
      { state: { status: 'active' }, message: { type: 'lessonCompleted' } },
      ({ state, message }) => startLessonWrite(state, message)
    )
    .with(
      {
        state: { status: 'active', progress: { status: 'loadFailed' } },
        message: { type: 'retryProgressLoad' }
      },
      ({ state }) => ({
        ...state,
        progress: {
          status: 'reconciling',
          completedLessonIds: state.progress.completedLessonIds
        }
      })
    )
    .with(
      {
        state: P.union(
          { status: 'active', progress: { status: 'saving' } },
          { status: 'finishing', progress: { status: 'saving' } }
        ),
        message: { type: 'lessonWriteSettled' }
      },
      ({ state, message }) => settleLessonWrite(state, message.lessonId, message.result)
    )
    .with(
      {
        state: {
          status: 'active',
          completion: { status: 'saving' }
        },
        message: { type: 'courseCompletionSettled', result: 'completed' }
      },
      ({ state }) => ({ ...state, completion: { status: 'completed' } })
    )
    .with(
      {
        state: { status: 'active', completion: { status: 'saving' } },
        message: { type: 'courseCompletionSettled', result: 'failed' }
      },
      ({ state }) => ({ ...state, completion: { status: 'failed' } })
    )
    .with(
      {
        state: { status: 'finishing', completion: { status: 'saving' } },
        message: { type: 'courseCompletionSettled' }
      },
      ({ state }) => ({ status: 'inactive', route: null, currentUserId: state.currentUserId })
    )
    .with(
      {
        state: { status: 'active', completion: { status: 'failed' } },
        message: { type: 'retryMarkCourseCompleted' }
      },
      ({ state }) => {
        if (!courseLessonsCompleted(completedLessonIds(state))) {
          return state
        }
        return startCourseCompletion(state)
      }
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function synchronizeCourse(
  state: CoursesState,
  route: ObservedCourseRoute,
  currentUserId: string | null
): CoursesState {
  if (!route || !currentUserId) {
    if (!route && currentUserId && state.status == 'active' && courseWriteRunning(state)) {
      return { ...state, status: 'finishing', route: null }
    }
    if (!route && state.status == 'finishing' && currentUserId == state.currentUserId) {
      return state
    }
    return { status: 'inactive', route, currentUserId }
  }
  if (state.status == 'active' && state.currentUserId == currentUserId) {
    return { ...state, route }
  }
  if (state.status == 'finishing' && state.currentUserId == currentUserId) {
    return { ...state, status: 'active', route }
  }
  return {
    status: 'active',
    route,
    currentUserId,
    progress: { status: 'loading' },
    completion: { status: 'incomplete' }
  }
}

function courseWriteRunning(state: ActiveCoursesState) {
  return state.progress.status == 'saving' || state.completion.status == 'saving'
}

function progressLoaded(
  state: ActiveCoursesState & {
    progress: Extract<CourseProgress, { status: 'loading' | 'reconciling' }>
  },
  message: Extract<CoursesMessage, { type: 'progressLoaded' }>
): UpdateResult<CoursesState, CoursesMessage> {
  let completion = state.completion
  if (message.completed || completion.status == 'completed') {
    completion = { status: 'completed' }
  }
  const ready: ActiveCoursesState = {
    ...state,
    progress: { status: 'ready', completedLessonIds: message.completedLessonIds },
    completion: normalizeFailedCompletion(completion, message.completedLessonIds)
  }

  if (
    ready.completion.status == 'incomplete' &&
    courseLessonsCompleted(message.completedLessonIds)
  ) {
    return startCourseCompletion(ready)
  }
  return ready
}

function progressLoadFailed(
  state: ActiveCoursesState & {
    progress: Extract<CourseProgress, { status: 'loading' | 'reconciling' }>
  }
): CoursesState {
  let completedLessonIds: string[] = []
  if (state.progress.status == 'reconciling') {
    completedLessonIds = state.progress.completedLessonIds
  }
  return {
    ...state,
    progress: { status: 'loadFailed', completedLessonIds }
  }
}

function startLessonWrite(
  state: ActiveCoursesState,
  message: Extract<CoursesMessage, { type: 'lessonCompleted' }>
): UpdateResult<CoursesState, CoursesMessage> {
  let baseCompletedLessonIds = completedLessonIdsFromProgress(state.progress)
  let writes: LessonWrite[] = []
  if (state.progress.status == 'saving') {
    baseCompletedLessonIds = state.progress.baseCompletedLessonIds
    writes = [...state.progress.writes]
  }
  if (completedLessonIds(state).includes(message.lessonId)) return state

  writes.push({ lessonId: message.lessonId, status: 'saving' })
  const projectedLessonIds = applyLessonWrites(baseCompletedLessonIds, writes)

  return [
    {
      ...state,
      progress: { status: 'saving', baseCompletedLessonIds, writes },
      completion: normalizeFailedCompletion(state.completion, projectedLessonIds)
    },
    saveLessonCompletion(message.lessonId)
  ]
}

function settleLessonWrite(
  state:
    | (ActiveCoursesState & { progress: Extract<CourseProgress, { status: 'saving' }> })
    | (FinishingCoursesState & {
        progress: Extract<CourseProgress, { status: 'saving' }>
      }),
  lessonId: string,
  status: 'saved' | 'failed'
): UpdateResult<CoursesState, CoursesMessage> {
  if (
    !state.progress.writes.some((write) => write.lessonId == lessonId && write.status == 'saving')
  ) {
    throw new Error(`Cannot settle inactive lesson write: ${lessonId}`)
  }

  const writes = state.progress.writes.map((write) => {
    if (write.lessonId == lessonId) {
      return { ...write, status }
    }
    return write
  })
  const completedLessonIds = applyLessonWrites(state.progress.baseCompletedLessonIds, writes)
  const completion = normalizeFailedCompletion(state.completion, completedLessonIds)

  if (writes.some((write) => write.status == 'saving')) {
    return { ...state, progress: { ...state.progress, writes }, completion }
  }
  if (state.status == 'finishing') {
    if (completion.status == 'incomplete' && courseLessonsCompleted(completedLessonIds)) {
      return startFinishingCourseCompletion(state, completedLessonIds)
    }
    return { status: 'inactive', route: null, currentUserId: state.currentUserId }
  }
  return {
    ...state,
    progress: { status: 'reconciling', completedLessonIds },
    completion
  }
}

function startCourseCompletion(
  state: ActiveCoursesState
): UpdateResult<CoursesState, CoursesMessage> {
  return [{ ...state, completion: { status: 'saving' } }, markCourseCompleted()]
}

function startFinishingCourseCompletion(
  state: FinishingCoursesState,
  completedLessonIds: string[]
): UpdateResult<CoursesState, CoursesMessage> {
  return [
    {
      status: 'finishing',
      route: null,
      currentUserId: state.currentUserId,
      progress: { status: 'ready', completedLessonIds },
      completion: { status: 'saving' }
    },
    markCourseCompleted()
  ]
}

function saveLessonCompletion(lessonId: string) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const snackbar = yield* Snackbar
    const saved = yield* runSdkRequest(
      sdk.send({
        type: 'courses/setLessonCompletion',
        courseId: oneHourCourseId,
        lessonId,
        completed: true
      })
    )

    if (!saved.ok) {
      yield* snackbar.show('Failed to update course progress', { variant: 'error' })
      return { type: 'lessonWriteSettled', lessonId, result: 'failed' } as const
    }
    return { type: 'lessonWriteSettled', lessonId, result: 'saved' } as const
  })
}

function markCourseCompleted() {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const snackbar = yield* Snackbar

    const completed = yield* runSdkRequest(
      sdk.send({ type: 'courses/markCourseCompleted', courseId: oneHourCourseId })
    )
    if (!completed.ok) {
      yield* snackbar.show('Failed to mark course completed', { variant: 'error' })
      return { type: 'courseCompletionSettled', result: 'failed' } as const
    }
    return { type: 'courseCompletionSettled', result: 'completed' } as const
  })
}

export function courseView(state: CoursesState): {
  completedLessonIds: string[]
  completionStatus: CourseCompletion['status']
  progressLoadFailed: boolean
} {
  if (state.status != 'active') {
    return {
      completedLessonIds: [],
      completionStatus: 'incomplete',
      progressLoadFailed: false
    }
  }
  return {
    completedLessonIds: completedLessonIds(state),
    completionStatus: state.completion.status,
    progressLoadFailed: state.progress.status == 'loadFailed'
  }
}

function completedLessonIds(state: ActiveCoursesState): string[] {
  return completedLessonIdsFromProgress(state.progress)
}

function completedLessonIdsFromProgress(progress: CourseProgress): string[] {
  return match(progress)
    .returnType<string[]>()
    .with({ status: 'loading' }, () => [])
    .with(
      { status: P.union('loadFailed', 'ready', 'reconciling') },
      ({ completedLessonIds }) => completedLessonIds
    )
    .with({ status: 'saving' }, ({ baseCompletedLessonIds, writes }) =>
      applyLessonWrites(baseCompletedLessonIds, writes)
    )
    .exhaustive()
}

function applyLessonWrites(baseCompletedLessonIds: string[], writes: LessonWrite[]) {
  const completed = new Set(baseCompletedLessonIds)
  for (const write of writes) {
    if (write.status != 'failed') {
      completed.add(write.lessonId)
    }
  }
  return [...completed].sort()
}

function normalizeFailedCompletion(
  completion: CourseCompletion,
  completedLessonIds: string[]
): CourseCompletion {
  if (completion.status == 'failed' && !courseLessonsCompleted(completedLessonIds)) {
    return { status: 'incomplete' }
  }
  return completion
}

function courseLessonsCompleted(completedLessonIds: string[]) {
  const completed = new Set(completedLessonIds)
  return oneHourCourseLessonIds.every((lessonId) => completed.has(lessonId))
}

function unexpectedMessage(state: CoursesState, message: CoursesMessage): never {
  let progress = ''
  if (state.status == 'active') {
    progress = `/${state.progress.status}`
  } else if (state.status == 'finishing') {
    progress = `/${state.progress.status}`
  }
  throw new Error(`Unexpected courses message ${message.type} while ${state.status}${progress}`)
}
